/**
 * emparejarMinutas.js — v2
 *
 * Decide si una minuta guardada y una reunión son la misma cosa.
 *
 * Hace falta porque el inicio junta dos fuentes —las citas del calendario y las
 * minutas de la base de datos— y una minuta generada a partir de una reunión
 * aparecía como una tarjeta aparte, con el mismo título y la misma fecha. Para
 * quien mira eso es un duplicado, aunque por dentro sean dos registros.
 *
 * Desde ahora cada minuta guarda de qué reunión salió (`recordingId`), así que
 * el emparejamiento es exacto. La comparación por título y fecha existe solo
 * para las minutas creadas antes de eso; sin ella, todo lo guardado hasta hoy
 * seguiría duplicado para siempre.
 *
 * Lo que NO debe hacer: colapsar dos ocurrencias distintas de una reunión
 * periódica. Se repiten el título cada semana y son minutas legítimamente
 * distintas; por eso la comparación por título exige además el mismo día.
 */

/** Sin tildes, sin mayúsculas y sin puntuación: "Plática." y "platica" son lo mismo. */
export function normalizarTitulo(texto) {
  return String(texto || '')
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** El día local de una fecha, como "2026-09-11". Null si no hay fecha. */
export function mismoDia(fecha) {
  if (!fecha) return null;
  const d = new Date(fecha);
  if (isNaN(d.getTime())) return null;
  const dd = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${dd(d.getMonth() + 1)}-${dd(d.getDate())}`;
}

/** El identificador de reunión que lleva una minuta, si lo lleva. */
function idDeReunionDe(minuta) {
  return (minuta && minuta.recordingId) || null;
}

/** El identificador de una reunión, venga del calendario o de una grabación. */
export function idDeReunion(reunion) {
  if (!reunion) return null;
  if (reunion.id) return reunion.id;
  return null;
}

/**
 * ¿Esta minuta salió de esta reunión?
 *
 * @param {Object} minuta - la de la API (`recordingId`, `title`, `meetingDate`)
 * @param {Object} reunion - la del calendario o de una grabación
 */
export function mismaReunion(minuta, reunion) {
  if (!minuta || !reunion) return false;

  const idMinuta = idDeReunionDe(minuta);
  const idReunion = idDeReunion(reunion);

  // Camino exacto: las minutas nuevas saben de dónde vienen.
  if (idMinuta && idReunion) return idMinuta === idReunion;
  if (idMinuta && !idReunion) return false;

  // Camino heredado: título igual y mismo día. Exigir el día es lo que impide
  // fundir dos sesiones de una reunión semanal en una sola tarjeta.
  const tituloIgual = normalizarTitulo(minuta.title) === normalizarTitulo(reunion.titulo);
  if (!tituloIgual) return false;

  const diaMinuta = mismoDia(minuta.meetingDate);
  const diaReunion = mismoDia(reunion.inicio);

  // Una minuta antigua puede no tener fecha de reunión (se guardaba sin ella).
  // En ese caso el título es lo único que hay: se acepta, porque el mal menor
  // es fundirla con su reunión y no dejarla duplicada.
  if (!diaMinuta) return true;

  return diaMinuta === diaReunion;
}

/**
 * Agrupa minutas y reuniones en una sola lista sin repeticiones.
 *
 * Cada entrada del resultado es una reunión con su minuta dentro (si la tiene),
 * o una minuta suelta cuando su reunión ya no está en el rango consultado.
 *
 * @param {Array} minutas - las de la API
 * @param {Array} reuniones - las del calendario, ya con su grabación si la hay
 * @returns {{entradas: Array, sueltas: Array}}
 */
export function unir(minutas, reuniones) {
  const usadas = new Set();
  const suyasDe = reuniones.map(reunion => {
    const encontradas = minutas.filter(m => mismaReunion(m, reunion));
    encontradas.forEach(m => usadas.add(m.minuteId));
    return encontradas;
  });

  // Segunda pasada: las que quedaron sueltas y llevan el título de UNA sola
  // reunión de la lista.
  //
  // Hace falta porque la fecha que guarda una minuta no siempre es la de la
  // reunión. La pantalla v1 escribe en `MeetingDate` el momento en que se
  // subió la grabación, o directamente la hora de guardar —se reconoce a
  // simple vista: una reunión no empieza a las 13:04:13.113—. Esas minutas
  // tienen el título correcto y la fecha equivocada, así que la comparación
  // por día las dejaba fuera y aparecían como una tarjeta más, duplicando su
  // reunión.
  //
  // Se exige que el título sea de una sola reunión porque es justo lo que
  // protegía el día: en una reunión semanal hay varias sesiones con el mismo
  // nombre y fundirlas sería perderlas. Si solo hay una, no hay nada que
  // confundir.
  //
  // Lo que se acepta a cambio: si la reunión a la que pertenecía de verdad
  // quedó fuera del rango consultado y hay otra del mismo nombre dentro, la
  // minuta se cuelga de esa. Sigue estando a un clic, y el resultado es mejor
  // que la tarjeta repetida que se ve hoy.
  for (const minuta of minutas) {
    if (usadas.has(minuta.minuteId)) continue;

    // Una minuta que sí dice de qué reunión salió ya se comparó por ahí y no
    // era de ninguna de estas. Colgarla de otra por parecerse en el título
    // sería contradecir el único dato fiable que tiene: esta pasada existe
    // para las que NO lo guardan, no para corregir a las que sí.
    if (minuta.recordingId) continue;

    const suTitulo = normalizarTitulo(minuta.title);
    if (!suTitulo) continue;

    const candidatas = [];
    reuniones.forEach((r, i) => {
      if (normalizarTitulo(r.titulo) === suTitulo) candidatas.push(i);
    });
    if (candidatas.length !== 1) continue;

    suyasDe[candidatas[0]].push(minuta);
    usadas.add(minuta.minuteId);
  }

  const entradas = reuniones.map((reunion, i) => {
    // Si hay varias minutas de la misma reunión (pasaba antes de guardar el
    // identificador), se enseña la más reciente y se dice cuántas hay.
    const suyas = suyasDe[i]
      .sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0));

    return {
      reunion,
      minuta: suyas[0] || null,
      minutasRepetidas: suyas.length > 1 ? suyas.length : 0
    };
  });

  const sueltas = minutas.filter(m => !usadas.has(m.minuteId));

  return { entradas, sueltas };
}

const emparejarMinutas = { normalizarTitulo, mismoDia, mismaReunion, idDeReunion, unir };
export default emparejarMinutas;
